<template>
  <Action
    v-model:visible-action-modal="visibleActionModal"
    :action="mappedAction"
    :completed="completed"
    @click.stop="startCommenting"
  >
    <slot name="title"> </slot>
    <div class="white-space-pre font-bold">
      <slot name="description">{{ action.description }}</slot>
    </div>
    <TaskCommentSection
      ref="taskCommentSection"
      class="mr-4 hideOnMobile"
      :task="action"
      :archived="archived"
      :original-board-id="originalBoardId"
    />

    <template #headerAdditions>
      <AttachmentBadge :files="action.Folder?.files" />
    </template>

    <template #oneLineContent>
      <slot name="oneLineContent">
        {{ oneLineContent }}
      </slot>
    </template>

    <template v-for="(_, slot) of $slots" #[slot]="scope">
      <slot :name="slot" v-bind="scope" />
    </template>
  </Action>
</template>

<script setup lang="ts">
import { computed, type PropType, ref } from "vue";
import {
  TaskStatus,
  type ActionListActionFields_Task_Fragment,
  type EventListEventFieldsFragment,
} from "@/graphql";
import AttachmentBadge from "@/components/badges/AttachmentBadge.vue";
import Action from "../action/ActionComponent.vue";
import TaskCommentSection from "./TaskCommentSection.vue";

const props = defineProps({
  action: {
    type: Object as PropType<ActionListActionFields_Task_Fragment>,
    required: true,
  },
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
});
defineSlots();

const visibleActionModal = defineModel("visibleActionModal", {
  type: Boolean,
  default: false,
});

defineEmits(["update:visibleActionModal"]);
const taskCommentSection = ref<InstanceType<typeof TaskCommentSection> | null>(null);

const mappedAction = computed(() => ({
  ...props.action,
}));
const oneLineContent = computed(() => {
  return props.action.description?.split("\n")[0] ?? "";
});

const completed = computed(() => {
  return props.action.status === TaskStatus.act;
});
const archived = computed(() => {
  return props.event.deletedAt != null;
});
function startCommenting() {
  // check if click event is a selection, then do nothing
  const selectionLength = window.getSelection()?.toString().length ?? 0;
  if (selectionLength === 0) {
    taskCommentSection.value?.startCommenting();
  }
}
const originalBoardId = computed(() => {
  return "boardConnection" in props.event
    ? (props.event.boardConnection as any).find((con: { status: number }) => con.status === 0)
        ?.boardId
    : undefined;
});
</script>

<style lang="scss" scoped>
.commentButton {
  position: absolute;
  bottom: 0;
  right: 20px;
  padding: 3px;
  cursor: pointer;
}
</style>
